import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

// Accordion
class AccordionCustom extends HTMLElement {
  /** @returns {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector('details');
    if (!(details instanceof HTMLDetailsElement)) {
      throw new Error('Details element not found');
    }
    return details;
  }

  /** @returns {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector('summary');
    if (!(summary instanceof HTMLElement)) {
      throw new Error('Summary element not found');
    }
    return summary;
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  #controller = new AbortController();

  #initialized = false;

  connectedCallback() {
    const { signal } = this.#controller;

    if (this.querySelector('details') instanceof HTMLDetailsElement) {
      this.#init();
    } else {
      const observer = new MutationObserver(() => {
        if (this.querySelector('details') instanceof HTMLDetailsElement) {
          observer.disconnect();
          this.#init();
        }
      });
      observer.observe(this, { childList: true, subtree: true });
      signal.addEventListener('abort', () => observer.disconnect(), { once: true });
    }
  }

  #init() {
    if (this.#initialized) return;
    this.#initialized = true;

    const { signal } = this.#controller;

    this.#setDefaultOpenState();

    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.summary.addEventListener('click', this.handleClick, { signal });
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });

    this.readMoreButtons = this.querySelector('.read-more-link');
    this.readMoreButtons?.addEventListener('click', this.#handleReadMoreClick, { signal });

    this.readLessButtons = this.querySelector('.read-less-link');
    this.readLessButtons?.addEventListener('click', this.#handleReadLessClick, { signal });
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  /**
   * @param {MouseEvent} event
   */
  handleClick = (event) => {
    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
      event.preventDefault();
    }
  };

  #handleMediaQueryChange = () => {
    // Breakpoint changes should re-apply the configured default.
    this.#setDefaultOpenState(true);
  };

  /**
   * Applies the configured open/closed default for the current breakpoint.
   * On first init, do not force-close a panel the user already opened before JS loaded.
   * @param {boolean} [force=false]
   */
  #setDefaultOpenState(force = false) {
    const isMobile = isMobileBreakpoint();
    const shouldBeOpenByDefault =
      (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
      (!isMobile && this.hasAttribute('open-by-default-on-desktop'));

    if (force || shouldBeOpenByDefault || !this.details.open) {
      this.details.open = shouldBeOpenByDefault;
    }
  }

  /**
   * @param {KeyboardEvent} event
   */
  #handleKeyDown = (event) => {
    if (event.key === 'Escape' && this.#closeWithEscape) {
      event.preventDefault();
      this.details.open = false;
      this.summary.focus();
    }
  };

  /**
   * @param {HTMLElement} element
   * @returns {HTMLElement|null}
   */
  #getDetailsContentSibling(element) {
    if (element.nextElementSibling instanceof HTMLElement &&
        element.nextElementSibling.classList.contains('details-content')) {
      return element.nextElementSibling;
    }

    if (element.previousElementSibling instanceof HTMLElement &&
        element.previousElementSibling.classList.contains('details-content')) {
      return element.previousElementSibling;
    }

    return null;
  }

  /**
   * @param {MouseEvent} event
   */
  #handleReadMoreClick = (event) => {
    event.preventDefault();

    const target = /** @type {HTMLElement|null} */ (event.target);
    if (!target || !target.parentElement) return;

    const parent = target.parentElement;
    const detailsContent = this.#getDetailsContentSibling(parent);

    parent.classList.add('collapsed');
    detailsContent?.classList.add('collapsed');

    target.classList.add('hidden');
    this.readLessButtons?.classList.remove('hidden');
  };

  /**
   * @param {MouseEvent} event
   */
  #handleReadLessClick = (event) => {
    event.preventDefault();

    const target = /** @type {HTMLElement|null} */ (event.target);
    if (!target || !target.parentElement) return;

    const parent = target.parentElement;
    const detailsContent = this.#getDetailsContentSibling(parent);

    parent.classList.remove('collapsed');
    detailsContent?.classList.remove('collapsed');

    target.classList.add('hidden');
    this.readMoreButtons?.classList.remove('hidden');
  };
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}
